// Recovers from two well-known browser-environment failures that crash the SPA:
//
// 1. Google Translate (and similar extensions) moves or replaces the DOM text
//    nodes it translates. React 18 then throws NotFoundError in removeChild /
//    insertBefore during commit and unmounts the whole tree (Sentry issues
//    INVITATIIONLINE-17/18/19/1A/1D). Patching the two Node methods to tolerate
//    already-moved nodes keeps React alive — see facebook/react#11538.
//
// 2. After a redeploy, cached HTML can reference old hashed chunks that no
//    longer exist on S3. Vite emits `vite:preloadError` when a dynamic import
//    fails — reload once to pick up the new build instead of leaving the user
//    on a broken lazy route (INVITATIIONLINE-1C).

export function installDomGuards(): void {
  if (typeof Node === 'function' && Node.prototype) {
    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
      if (child.parentNode !== this) {
        console.warn('Ignored removeChild on a node that is no longer a child (translated DOM?)', child);
        return child;
      }
      return originalRemoveChild.call(this, child) as T;
    };

    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function <T extends Node>(
      this: Node,
      newNode: T,
      referenceNode: Node | null,
    ): T {
      if (referenceNode && referenceNode.parentNode !== this) {
        console.warn('Ignored insertBefore with a detached reference node (translated DOM?)', referenceNode);
        return newNode;
      }
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    };
  }

  const RELOAD_FLAG = 'chunk-reload-at';
  window.addEventListener('vite:preloadError', (event) => {
    const lastReload = Number(sessionStorage.getItem(RELOAD_FLAG) ?? '0');
    // Reload at most once per minute so a genuinely broken deploy can't loop.
    if (Date.now() - lastReload > 60_000) {
      sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
      event.preventDefault();
      window.location.reload();
    }
  });
}
